<template>
  <div>
    <div class="md-layout">
      <div class="md-layout-item">
        <div class="btn-row">
          <md-button class="md-success" @click="addBridge">
            {{ $t('button.add') }}
          </md-button>
        </div>
        <md-card>
          <transition name="fade">
            <div v-if="isBridgesDataLoading" class="report-load-progress">
              <md-progress-bar
                  md-mode="indeterminate"
                  class="progress-bar"
              ></md-progress-bar>
            </div>
          </transition>
          <md-card-header
              class="md-card-header-icon md-card-header-green card-icon"
          >
            <div class="card-icon">
              <md-icon>view_list</md-icon>
            </div>
            <h4 class="title">{{ $t('label.bridges') }}</h4>
            <div class="report-export-icon">
              <div @click='onSelectColumn'>
                <i class='fas fa-columns'></i>
              </div>
              <div @click='onExport'>
                <i class='fas fa-file-export'></i>
              </div>
            </div>
          </md-card-header>

          <md-card-content>
            <md-table
                :value="queriedData"
                :md-sort.sync="currentSort"
                :md-sort-order.sync="currentSortOrder"
                :md-sort-fn="customSort"
                class="paginated-table table-striped table-hover"
            >
              <md-table-toolbar>
                <md-field>
                  <label for="pages">{{ $t('label.per_page') }}</label>
                  <md-select
                      @input="onChangePerPage"
                      :value="pagination.perPage"
                      name="pages"
                  >
                    <md-option
                        v-for="item in pagination.perPageOptions"
                        :key="item"
                        :label="item"
                        :value="item"
                    >
                      {{ item === 'all' ? $t('reports.table_all') : item }}
                    </md-option>
                  </md-select>
                </md-field>

                <md-field>
                  <md-input
                      type="search"
                      class="mb-3"
                      clearable
                      style="width: 200px"
                      :placeholder="$t('label.search_records')"
                      v-model="searchQuery"
                  ></md-input>
                </md-field>
              </md-table-toolbar>
              <md-table-row slot="md-table-row" slot-scope="{item}">
                <md-table-cell
                    :md-label="$t('tables.actions')"
                >
                  <div class="cell-actions">
                    <md-button
                        class="md-raised md-sm md-primary"
                        @click.stop.prevent="onEditRow(item[0])"
                    >
                      {{ $t('button.view_edit') }}
                    </md-button>

                    <md-button
                        class="md-raised md-sm md-danger"
                        @click.stop.prevent="onDelete(item[0])"
                    >
                      {{ $t('button.delete') }}
                    </md-button>
                  </div>
                </md-table-cell>
                <md-table-cell
                    v-for='cellHeader of visibleHeaders'
                    :key='cellHeader'
                    :md-label='$t(`translate.${cellHeader}`)'
                    :md-sort-by='cellHeader'
                    :md-tooltip='$t(`translate.${cellHeader}`)'

                >
                  <template v-if='["bridge_id", "road_name", "roadcode" ].includes(cellHeader)'>
                    <div :title='item[headerId(cellHeader)]'>
                      {{ item[headerId(cellHeader)] ? item[headerId(cellHeader)].toUpperCase() : "" }}
                    </div>
                  </template>
                  <template v-else>
                    <div class='wrapped-cell'
                         :title="!['inventory_insp_date', 'created_at', 'condition_insp_date'].includes(cellHeader) ?
                       item[headerId(cellHeader)] : `${item[headerId(cellHeader)]}`.substring(0, 10)">

                      {{
                        !['inventory_insp_date', 'created_at', 'condition_insp_date'].includes(cellHeader) ?
                            item[headerId(cellHeader)] : `${item[headerId(cellHeader)]}`.substring(0, 10)
                      }}
                    </div>
                  </template>
                </md-table-cell>
              </md-table-row>
            </md-table>
          </md-card-content>
          <md-card-actions md-alignment="space-between">
            <div class>
              <p class="card-category">
                {{
                  $t('label.showing_from_to_of_entries', {
                    from: to === 0 ? 0 : from + 1,
                    to: to,
                    total
                  })
                }}
              </p>
            </div>
            <pagination
                class="pagination-no-border pagination-success"
                v-model="pagination.currentPage"
                :per-page="pagination.perPage"
                :total="total"
            ></pagination>
          </md-card-actions>
        </md-card>
      </div>
    </div>
    <md-dialog :md-active.sync='showColumnList'>
      <md-dialog-content>
        <md-list class='md-triple-line'>
          <div v-for='column in bridges_headers' :key='column'>
            <md-checkbox v-model='visibleHeaders' :value='column'>{{ column }}</md-checkbox>
          </div>
        </md-list>
      </md-dialog-content>
      <md-dialog-actions>
        <md-button class='md-primary' @click='showColumnList = false'>{{ $t('button.close') }}</md-button>
      </md-dialog-actions>
    </md-dialog>
  </div>
</template>
<script>
import {mapState} from 'vuex'
import {Pagination} from '@/components'
import XLSX from 'xlsx'
import Swal from "sweetalert2";

export default {
  data() {
    return {
      visibleHeaders: [],
      showColumnList: false,
      isBridgesDataLoading: true,
      reportName: "bridges",
      currentSort: 'id',
      currentSortOrder: 'asc',
      bridgesList: {headers: [], rows: []},
      pagination: {
        perPage: 10,
        currentPage: 1,
        perPageOptions: [5, 10, 25, 50, 'all'],
        total: 0
      },
      searchQuery: '',
      propsToSearch: ['key'],
      searchedData: [],
      fuseSearch: null
    }
  },
  components: {
    Pagination
  },
  async created() {
    this.visibleHeaders = ["bridge_id", "bridge_name", "roadcode", "bridge_condition_index", "bridge_priority_index"]
    this.isBridgesDataLoading = true
    this.bridgesList = await this.$store.dispatch("LOAD_BRIDGES_LIST")
    this.isBridgesDataLoading = false
  },

  computed: {
    ...mapState({}),
    bridges_headers() {
      return [...this.bridgesList.headers]
    },
    bridges_rows() {
      return [...this.bridgesList.rows]
    },
    queriedData() {
      const result = !this.searchQuery
          ? this.tableData
          : this.searchedData
              ? this.searchedData
              : []
      return result.slice(this.from, this.to)
    },
    exportedData() {
      const result = !this.searchQuery
          ? this.tableData
          : this.searchedData
              ? this.searchedData
              : []
      return result
    },
    to() {
      let highBound = this.from + this.pagination.perPage
      if (this.total < highBound) {
        highBound = this.total
      }
      return highBound
    },
    from() {
      return this.pagination.perPage * (this.pagination.currentPage - 1)
    },
    total() {
      return this.searchQuery ? this.searchedData.length : this.tableData.length
    },
    tableData() {
      return this.bridges_rows
    }
  },
  methods: {
    headerId(name) {
      return this.bridges_headers.findIndex(header => header === name)
    },
    addBridge() {
      this.$router.push({name: "bridge-add"})
    },
    onEditRow(item) {
      this.$router.push({name: "bridge-upd", params: {bid: item}})
    },

    onDelete(id) {
      const bridgeIdHeader = this.headerId("bridge_id")
      const alert = {
        icon: 'success',
        text: this.$t(`label.bridge_was_deleted`),
        footer: ''
      }
      Swal.fire({
        title: `${this.$t("label.are_you_sure")}`,
        text: `${this.$t("label.do_you_want_to_delete_bridge")} ${id}`,
        icon: "warning",
        showCancelButton: true,
        confirmButtonClass: "md-button md-danger btn-fill",
        cancelButtonClass: "md-button md-default btn-fill",
        confirmButtonText: `${this.$t("label.yes_delete_it")}`,
        buttonsStyling: false
      }).then(async result => {
        if (result.value) {
          try {
            await this.$store.dispatch("DELETE_BRIDGE", id)
            const ind = this.bridgesList.rows.findIndex(b => b[bridgeIdHeader] === id)
            this.bridgesList.rows.splice(ind, 1)
            Swal.fire({
              title: this.$t("label.deleted"),
              text: this.$t("label.bridge_was_deleted"),
              type: "success",
              confirmButtonClass: "md-button md-success btn-fill",
              buttonsStyling: false
            });
          } catch (err) {
            console.log({err})
            Swal.fire({
              title: this.$t("label.deleted"),
              text: this.$t(`label.bridge_was_not_deleted`),
              type: "error",
              confirmButtonClass: "md-button md-default btn-fill",
              buttonsStyling: false,
              footer:this.$t(err.message ? err.message : err)
            })
            //throw err
          }
        }
      });
    },

    onSelectColumn() {
      this.showColumnList = true
    },
    onExport() {
      const headers = this.bridges_headers.map((header) =>
          this.$t(`translate.${header}`)
      )
      const export_data = [headers, ...this.exportedData]
      const today = new Date().toJSON().slice(0, 10)
      const worksheet = XLSX.utils.aoa_to_sheet(export_data)
      const new_workbook = XLSX.utils.book_new()
      const filter_name = !this.searchQuery
          ? ''
          : `_filter(${this.searchQuery})`

      XLSX.utils.book_append_sheet(
          new_workbook,
          worksheet,
          `Report${filter_name}`
      )
      XLSX.writeFile(
          new_workbook,
          `export_${this.$t(this.reportName)}_${today}.xlsx`
      )
    },
    onChangePerPage(val) {
      const limit = val === 'all' ? this.total : val
      this.pagination.perPage = limit
    },
    isNumber(value) {
      return typeof value === 'number' && isFinite(value)
    },
    customSort(value) {
      const dataInd = this.bridges_headers.findIndex(
          (report) => report === this.currentSort
      )
      if (!~dataInd) {
        return
      }
      return value.sort((a, b) => {
        const sortBy = dataInd
        const aValue = a[sortBy] === null ? '' : a[sortBy].toString()
        const bValue = b[sortBy] === null ? '' : b[sortBy].toString()
        if (this.currentSortOrder === 'desc') {
          return aValue.localeCompare(bValue, undefined, {numeric: true})
        }
        return bValue.localeCompare(aValue, undefined, {numeric: true})
      })
    }
  },
  watch: {
    /**
     * Searches through the table data by a given query.
     * NOTE: If you have a lot of data, it's recommended to do the search on the Server Side and only display the results here.
     * @param value of the query
     */
    searchQuery(value) {
      let result = this.tableData
      const regex = new RegExp(`${value}`, 'i')
      if (value !== '') {
        result = this.tableData.filter((row) => {
          return row.reduce((incl, elem) => incl || regex.test(elem), false)
        }) //this.fuseSearch.search(this.searchQuery)
      }
      this.searchedData = result
    }
    // bridges_headers() {
    // }
  }
}
</script>

<style lang="scss">
@import '@/assets/scss/md/_variables.scss';

.cell-actions {
  display: flex;
  flex-direction: column;
  align-items: flex-start;

  .md-button {
    margin: 3px 0;
    min-width: 100px;
  }
}

.category-select {
  max-width: 300px;
  top: 20px;
  margin-left: auto;
}

.btn-row {
  width: 100%;
  display: flex;
  justify-content: flex-end;
}

.md-table-head-container {
  //text-align: center;
}

.report-load-progress {
  position: absolute;
  width: 100%;
  /* height: 100%; */
  top: 0px;
  display: flex;
  flex-direction: column;
  align-items: center;

  .progress-bar {
    width: 100%;
  }
}

.card-icon {
  z-index: 10;
}

.report-export-icon {
  display: flex;

  position: absolute;
  right: 10px;
  top: 15px;
  font-size: 25px;
  color: $gray-color;
  cursor: pointer;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.7s;
}

.fade-enter, .fade-leave-to /* .fade-leave-active до версии 2.1.8 */
{
  opacity: 0;
}
</style>
